import { LitElement, html, css } from '@hh/global.js'

class HHBannerVideo extends LitElement {
  static properties = {
    overlayTransparency: {
      type: Number,
    },
    isPlaying: {
      type: Boolean,
      state: true,
      reflect: true,
    },
    isMuted: {
      type: Boolean,
      state: true,
      reflect: true,
    },
    startMuted: {
      type: Boolean,
      reflect: true,
      attribute: 'start-muted',
    },
  }

  constructor() {
    super()

    this.overlayTransparency = 0.5
  }

  connectedCallback() {
    super.connectedCallback()

    if (this.startMuted !== undefined) {
      this.isMuted = this.startMuted
      this.isPlaying = true
    }
  }

  onClick(e) {
    const isPaused = this.videoEl.paused
    isPaused ? this.videoEl.play() : this.videoEl.pause()

    this.isPlaying = isPaused
  }

  onAudioClick(e) {
    this.videoEl.muted = !this.videoEl.muted
    this.isMuted = this.videoEl.muted
  }

  get videoEl() {
    return this.querySelector('video') ?? null
  }

  static styles = css`
    :host([theme='light']),
    :host([theme='light']) .hh-banner-video__controls-btn {
      color: var(--theme-color-reverse);
    }

    :host([theme='dark']),
    :host([theme='dark']) .hh-banner-video__controls-btn {
      color: var(--theme-color-ink);
    }

    :host([hide-mute]) .hh-banner-video__controls-btn--audio {
      display: none;
    }

    :host .hh-banner-video {
      position: var(--banner-video-position, relative);
      width: 100%;
      height: var(--banner-video-height, 100%);
    }

    :host([full-height-video]) .hh-banner-video {
      height: 100%;
    }

    .hh-banner-video__overlay {
      content: '';
      display: block;
      position: absolute;
      bottom: 0;
      top: 0;
      left: 0;
      right: 0;
      background: var(--banner-video-overlay);
      opacity: var(--overlay-transparency, 1);
    }

    :host(.overlay-custom-bg) .hh-banner-video__overlay {
      background: var(--hh-overlay-bg);
    }

    .hh-banner-video__controls-container {
      position: absolute;
      display: flex;
      column-gap: 1.6rem;
      bottom: 19px;
      right: 19px;
      z-index: 5;
      color: var(--theme-color-reverse);
    }

    :host([controls-left]) .hh-banner-video__controls-container {
      right: unset;
      left: 19px;
    }

    :host([controls-top-right]) .hh-banner-video__controls-container {
      bottom: unset;
      top: 19px;
      right: 19px;
    }

    @media (min-width: 768px) {
      .hh-banner-video__controls-container {
        column-gap: 2rem;
      }
    }

    .hh-banner-video__controls-btn {
      padding: 0;
      border: none;
      background: none;
      cursor: pointer;
    }

    .hh-banner-video__controls-btn--audio {
      min-width: 2.9rem;
    }

    .hh-banner-video__controls-btn[data-state='playing'] .hh-banner__play-icon,
    .hh-banner-video__controls-btn[data-state='paused'] .hh-banner__pause-icon,
    .hh-banner-video__controls-btn[data-state='muted'] .hh-banner__mute-icon,
    .hh-banner-video__controls-btn[data-state='unmuted']
      .hh-banner__unmute-icon {
      display: none;
    }
  `

  togglePlayTemplate() {
    return html`
      <button
        type="button"
        class="hh-banner-video__controls-btn"
        aria-label="Toggle video play/pause"
        @click="${this.onClick}"
        data-state="${this.isPlaying ? 'playing' : 'paused'}"
      >
        <div class="hh-banner__pause-icon" aria-hidden="true">
          <svg
            width="20"
            height="24"
            viewBox="0 0 20 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.661513 23.9988H7.1395C7.50546 23.9988 7.80098 23.719 7.80098 23.3726L7.80236 1.60468C7.80236 1.25825 7.50683 0.978516 7.14088 0.978516H0.66148C0.295511 0.978516 0 1.25826 0 1.60468V23.374C0 23.7191 0.296904 23.9988 0.66148 23.9988L0.661513 23.9988ZM1.32437 2.23084H6.47947V22.7465L1.32437 22.7452V2.23084Z"
              fill="currentColor"
            />
            <path
              d="M12.1992 1.60468V23.374C12.1992 23.7204 12.4947 24.0001 12.8607 24.0001H19.3387C19.7047 24.0001 20.0002 23.7204 20.0002 23.374L20.0015 1.60468C20.0015 1.25825 19.706 0.978516 19.3401 0.978516H12.8621C12.4961 0.978516 12.1992 1.25826 12.1992 1.60468ZM13.5222 2.23084H18.6759V22.7465L13.5222 22.7452V2.23084Z"
              fill="currentColor"
            />
          </svg>
        </div>
        <div class="hh-banner__play-icon" aria-hidden="true">
          <svg
            width="19"
            height="21"
            viewBox="0 0 19 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.04431 19.1119C2.21099 19.5881 1.17414 18.9864 1.17414 18.0266L1.17413 2.06222C1.17413 1.10244 2.21099 0.500733 3.04431 0.976916L17.0132 8.95913C17.8529 9.43899 17.8529 10.6499 17.0132 11.1297L3.04431 19.1119Z"
              stroke="currentColor"
              stroke-width="1.5"
            />
          </svg>
        </div>
      </button>
    `
  }

  toggleAudioTemplate() {
    return html`
      <button
        type="button"
        class="hh-banner-video__controls-btn hh-banner-video__controls-btn--audio"
        aria-label="Toggle video mute/unmute"
        @click="${this.onAudioClick}"
        data-state="${this.isMuted ? 'muted' : 'unmuted'}"
      >
        <div class="hh-banner__unmute-icon" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="27"
            height="24"
            viewBox="0 0 27 24"
            fill="none"
          >
            <path
              d="M1.48843 2.60929L6.00719 7.13615H1.80426C1.37471 7.13615 0.962748 7.30679 0.659009 7.61053C0.35527 7.91427 0.184631 8.32622 0.184631 8.75578V15.2343C0.184631 15.6638 0.35527 16.0758 0.659009 16.3795C0.962748 16.6833 1.37471 16.8539 1.80426 16.8539H5.9667L11.9674 22.9032C12.1249 23.049 12.3099 23.162 12.5115 23.2357C12.713 23.3094 12.9273 23.3423 13.1417 23.3324C13.3528 23.3324 13.5618 23.2912 13.7571 23.2109C14.0542 23.0889 14.3082 22.8813 14.487 22.6146C14.6658 22.3478 14.7613 22.0339 14.7613 21.7128V15.8578L19.5311 20.6276C19.3286 20.7491 19.1343 20.8625 18.9237 20.9678C18.7315 21.0644 18.5855 21.2335 18.518 21.4377C18.4504 21.642 18.4667 21.8647 18.5634 22.057C18.66 22.2492 18.8291 22.3951 19.0333 22.4627C19.2376 22.5303 19.4603 22.514 19.6526 22.4173C20.0165 22.2336 20.3707 22.0308 20.7134 21.81L22.5598 23.6563L23.6692 22.5226L2.61407 1.46745L1.48843 2.60929ZM13.1417 21.7128L6.88179 15.4205C6.72135 15.2866 6.51533 15.2198 6.30682 15.2343H1.80426V8.75578H6.37161C6.47875 8.75533 6.58474 8.73363 6.68344 8.69193C6.78214 8.65022 6.87158 8.58935 6.94658 8.51283L7.15713 8.30228L13.1417 14.2706V21.7128Z"
              fill="currentColor"
            />
            <path
              d="M18.7213 2.83602C20.1327 3.49527 21.3718 4.47338 22.3407 5.69323C23.3096 6.91309 23.982 8.34134 24.3047 9.86538C24.6274 11.3894 24.5917 12.9676 24.2003 14.4755C23.809 15.9834 23.0727 17.3797 22.0496 18.5545L23.1996 19.7044C24.4074 18.346 25.2814 16.7243 25.752 14.9686C26.2226 13.2128 26.2768 11.3714 25.9104 9.59101C25.5439 7.81059 24.7669 6.1403 23.6411 4.71312C22.5153 3.28594 21.0719 2.14128 19.4258 1.37026C19.3297 1.32046 19.2246 1.29043 19.1167 1.28195C19.0088 1.27347 18.9003 1.28672 18.7975 1.32089C18.6948 1.35507 18.6 1.40949 18.5187 1.48094C18.4374 1.55239 18.3712 1.63942 18.3241 1.73687C18.277 1.83433 18.2499 1.94024 18.2445 2.04835C18.239 2.15645 18.2553 2.26456 18.2923 2.36627C18.3293 2.46799 18.3863 2.56125 18.46 2.64054C18.5337 2.71983 18.6225 2.78355 18.7213 2.82792V2.83602Z"
              fill="currentColor"
            />
            <path
              d="M16.9397 7.63825C17.7019 8.08298 18.335 8.71869 18.7767 9.48269C19.2183 10.2467 19.4532 11.1126 19.4582 11.995C19.4582 13.0789 19.1058 14.1334 18.454 14.9995L19.6202 16.1494C20.5672 14.9723 21.0818 13.5058 21.0778 11.995C21.0726 10.8295 20.7636 9.68551 20.1813 8.67582C19.5991 7.66614 18.7637 6.82565 17.7576 6.23727C17.6654 6.17868 17.5624 6.13933 17.4546 6.12161C17.3468 6.10388 17.2366 6.10814 17.1305 6.13412C17.0244 6.16011 16.9247 6.20728 16.8373 6.27281C16.7499 6.33833 16.6767 6.42086 16.622 6.51542C16.5674 6.60998 16.5324 6.71461 16.5192 6.82303C16.5061 6.93146 16.515 7.04142 16.5454 7.14632C16.5758 7.25121 16.6272 7.34886 16.6963 7.4334C16.7655 7.51794 16.851 7.58762 16.9478 7.63825H16.9397Z"
              fill="currentColor"
            />
            <path
              d="M13.1417 2.27726V9.68706L14.7613 11.3067V2.27726C14.7615 1.95626 14.6664 1.64241 14.4879 1.37558C14.3095 1.10875 14.0558 0.900962 13.759 0.778608C13.4622 0.656253 13.1358 0.624848 12.8211 0.688378C12.5065 0.751909 12.2178 0.90751 11.9917 1.13543L8.28278 4.84437L9.42461 5.98621L13.1417 2.27726Z"
              fill="currentColor"
            />
          </svg>
        </div>
        <div class="hh-banner__mute-icon" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="29"
            height="24"
            viewBox="0 0 29 24"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M14.291 1.05841C14.4609 1.13345 14.6054 1.25625 14.7068 1.41187C14.8082 1.56749 14.8622 1.74922 14.8622 1.93497V23.0184C14.8623 23.2042 14.8084 23.386 14.7071 23.5417C14.6058 23.6974 14.4614 23.8203 14.2915 23.8955C14.1215 23.9706 13.9335 23.9947 13.7501 23.9649C13.5667 23.9351 13.3959 23.8526 13.2586 23.7275L6.5055 17.5878H3.04271C2.44966 17.5878 1.88089 17.3522 1.46154 16.9329C1.04219 16.5135 0.806595 15.9447 0.806595 15.3517V9.60166C0.806595 8.36732 1.80838 7.36554 3.04271 7.36554H6.5055L13.2586 1.2258C13.3961 1.10109 13.5669 1.019 13.7501 0.989509C13.9334 0.96002 14.1213 0.9844 14.291 1.05969V1.05841ZM12.9455 4.10081L7.52134 9.03305C7.34477 9.19354 7.11467 9.28239 6.87606 9.28221H3.04271C2.95799 9.28221 2.87674 9.31587 2.81683 9.37578C2.75692 9.43568 2.72327 9.51694 2.72327 9.60166V15.3517C2.72327 15.528 2.86638 15.6711 3.04271 15.6711H6.87606C7.11467 15.671 7.34477 15.7598 7.52134 15.9203L12.9455 20.8525V4.10081Z"
              fill="currentColor"
            />
            <path
              d="M23.4463 2.53807C23.626 2.3586 23.8696 2.2578 24.1236 2.2578C24.3775 2.2578 24.6211 2.3586 24.8008 2.53807C30.2901 8.02742 30.2901 16.9259 24.8008 22.4152C24.6191 22.5845 24.3788 22.6767 24.1306 22.6723C23.8823 22.6679 23.6454 22.5673 23.4698 22.3918C23.2942 22.2162 23.1937 21.9793 23.1893 21.731C23.1849 21.4827 23.2771 21.2425 23.4463 21.0608C24.5737 19.9336 25.468 18.5953 26.0782 17.1224C26.6883 15.6495 27.0024 14.0709 27.0024 12.4767C27.0024 10.8824 26.6883 9.30376 26.0782 7.83088C25.468 6.35801 24.5737 5.01974 23.4463 3.89252C23.2669 3.71283 23.1661 3.46925 23.1661 3.21529C23.1661 2.96133 23.2669 2.71775 23.4463 2.53807Z"
              fill="currentColor"
            />
            <path
              d="M20.2838 7.05504C20.1948 6.96602 20.0891 6.89541 19.9728 6.84723C19.8565 6.79906 19.7319 6.77426 19.606 6.77426C19.4801 6.77426 19.3554 6.79906 19.2391 6.84723C19.1228 6.89541 19.0171 6.96602 18.9281 7.05504C18.8391 7.14406 18.7685 7.24974 18.7203 7.36605C18.6721 7.48236 18.6473 7.60701 18.6473 7.73291C18.6473 7.8588 18.6721 7.98345 18.7203 8.09976C18.7685 8.21607 18.8391 8.32175 18.9281 8.41077C19.4621 8.94471 19.8856 9.57859 20.1746 10.2762C20.4635 10.9738 20.6123 11.7216 20.6123 12.4767C20.6123 13.2318 20.4635 13.9795 20.1746 14.6771C19.8856 15.3748 19.4621 16.0086 18.9281 16.5426C18.7588 16.7242 18.6667 16.9645 18.671 17.2128C18.6754 17.4611 18.776 17.698 18.9516 17.8735C19.1272 18.0491 19.3641 18.1497 19.6123 18.1541C19.8606 18.1585 20.1009 18.0663 20.2826 17.897C21.7202 16.4593 22.5279 14.5093 22.5279 12.476C22.5279 10.4428 21.7202 8.49281 20.2826 7.05504H20.2838Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </button>
    `
  }

  render() {
    return html`
      <div class="hh-banner-video">
        <div class="hh-banner-video__overlay" aria-hidden="true"></div>
        <slot></slot>
        <div class="hh-banner-video__controls-container">
          ${this.togglePlayTemplate()} ${this.toggleAudioTemplate()}
        </div>
      </div>
    `
  }
}

customElements.define('hh-banner-video', HHBannerVideo)
